/** Typed fetch wrapper. Every error carries the server's human message. */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach the server. Check your connection.');
  }
  const type = res.headers.get('content-type') ?? '';
  const data = type.includes('application/json') ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    if (res.status === 401 && path !== '/api/auth/signin' && path !== '/api/auth/me') window.dispatchEvent(new Event('hv:signed-out'));
    throw new ApiError(res.status, data?.error?.code ?? 'error', data?.error?.message ?? `Request failed (${res.status}).`);
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b: unknown = {}) => request<T>('POST', p, b),
  put: <T>(p: string, b: unknown = {}) => request<T>('PUT', p, b),
  patch: <T>(p: string, b: unknown = {}) => request<T>('PATCH', p, b),
  del: <T>(p: string) => request<T>('DELETE', p),
};

export interface Me {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'demo';
  /** Set when this is a teammate signed in from AgeDash: the teammate's name. */
  teammateOf?: string | null;
  expiresAt: number | null;
  limits: { dailyCalls: number; maxCallSeconds: number; maxWebSeconds: number };
}

export interface AgentSummary {
  id: string;
  name: string;
  description: string;
  published: boolean;
  voice: string;
  brain: string;
  listening?: string;
  /** A teammate's own agent (made in AgeDash). */
  mine?: boolean;
}

export interface Agent extends AgentSummary {
  greeting: string;
  system_prompt: string;
  knowledge: string;
  llm_provider: 'groq' | 'gemini' | 'openai' | 'xai';
  llm_model: string;
  temperature: number;
  tts_provider: 'rumik' | 'elevenlabs' | 'sarvam';
  tts_model: string;
  tts_voice: string;
  tts_description: string;
  stt_provider: 'deepgram' | 'sarvam';
  stt_model: string;
  stt_language: string;
  endpointing_ms: number;
  updated_at: number;
}

export interface Capabilities {
  phone: boolean;
  phoneReason: string | null;
  allowedPrefixes: string[];
  voiceCompare: boolean;
  limits: { dailyCalls: number; callsUsed: number; maxCallSeconds: number; maxWebSeconds: number } | null;
}

export interface ConversationRow {
  id: string;
  channel: 'web' | 'phone_out' | 'phone_in';
  status: string;
  agent_name: string;
  phone: string;
  started_at: number;
  duration_ms: number;
  turns: number;
  interruptions?: number;
  cost_inr?: number;
  end_reason: string;
  user_email?: string | null;
  tts?: string;
  llm?: string;
  workspace_name?: string | null;
  link_title?: string | null;
}

/** A timed demo link, as the owner and teammates see it. */
export interface DemoLinkView {
  id: string;
  url: string;
  title: string;
  agentId: string;
  agentName: string;
  minutes: number;
  usedSeconds: number;
  remainingSeconds: number;
  state: 'active' | 'used' | 'expired' | 'revoked';
  web: boolean;
  phone: boolean;
  createdBy: string;
  createdAt: number;
  expiresAt: number;
  lastUsedAt: number | null;
  conversations: number;
  workspaceName?: string;
}

/** A teammate's AgeDash workspace. */
export interface WorkspaceView {
  id: string;
  name: string;
  groups: string[];
  status: 'active' | 'disabled';
  dailyCallLimit: number;
  note: string;
  createdAt: number;
  lastUsedAt: number | null;
  keyLast4: string;
  links: number;
  conversationsThisWeek: number;
  callsToday: number;
  ownKeys: { name: string; inForce: boolean }[];
}

export const fmt = {
  date: (t: number | null | undefined) =>
    t ? new Date(t).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—',
  day: (t: number | null | undefined) => (t ? new Date(t).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'),
  duration: (ms: number) => {
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  },
  inr: (n: number | null | undefined, digits = 2) =>
    n === null || n === undefined ? '—' : `₹${n.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`,
  ms: (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${Math.round(n)} ms`),
  channel: (c: string) => (c === 'web' ? 'Browser' : c === 'phone_out' ? 'Phone · outbound' : 'Phone · inbound'),
};
